import { Button } from '@/components/form/Button';
import { Product, PropertyBag } from '@/services/api/contracts/catalog/types';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { useBulkUpdateProducts, useProducts } from '@/services/api/hooks/product';
import { IncomingProduct, IncomingProductSpec } from '@/services/csv/helpers/parsers';
import produce from 'immer';
import { useCallback, useState } from 'react';
import { toProduct } from '../helpers/toProduct';

import { useControls } from '@/components/multistep/useMultistep';
import { useAddPriceAndMargin } from '@/services/api/hooks/margins';
import { useRegions } from '@/services/api/hooks/region';
import { useMap, useResult } from '@/services/csv/useCSVService';
import { getDescriptionFromRichTextArea } from '@/utils/richText';
import { toast } from 'sonner';
import { Result } from '../bulkUploadHelper';
import { Maps } from '../enum';
import { ExtendedBulkProduct, ProductReviewGrid } from './ProductReviewGrid';

const compareTo = (a: number, b: number, precision: number) => {
  return Math.abs(a - b) < precision;
};

const mismatchInSpecs = (incomingSpecs: IncomingProductSpec[], productSpecs: PropertyBag) => {
  const productSpecMap = new Map<string, string>();
  const unmatchedProductSpecs: PropertyBag = [];

  productSpecs.forEach(spec => {
    if (spec.iconId) {
      productSpecMap.set(spec.iconId, spec.text);
    } else {
      unmatchedProductSpecs.push(spec);
    }
  });

  let unmatchedIncomingSpecs = incomingSpecs.filter(spec => {
    if (!spec.type) return true;

    const expectedValue = productSpecMap.get(spec.type);
    if (expectedValue !== undefined) {
      return expectedValue !== spec.value;
    }

    return true;
  });

  unmatchedIncomingSpecs = unmatchedIncomingSpecs.filter(incomingSpec => {
    const matchIndex = unmatchedProductSpecs.findIndex(productSpec => productSpec.text === incomingSpec.value);
    if (matchIndex !== -1) {
      unmatchedProductSpecs.splice(matchIndex, 1);
      return false;
    }
    return true;
  });

  return unmatchedIncomingSpecs.length > 0 || unmatchedProductSpecs.length > 0;
};

const filterModifications = (
  incomingProducts: IncomingProduct[],
  catalog: Product[],
  regionMap: Record<string, string>
) => {
  const modifications = incomingProducts.filter((incoming: IncomingProduct) => {
    const regionId = regionMap[incoming.Region];

    const product = catalog.find(
      (catalogItem: Product) => catalogItem.productId === incoming.ManufacturerSKU && catalogItem.region.id === regionId
    );
    if (product !== undefined) {
      const margin = product.price?.margins[0];
      const price = product.price?.price;

      const productLookupDescription = product.description?.value
        ? getDescriptionFromRichTextArea(product.description?.value)
        : '';
      const incomingDescription = incoming.Description ?? '';

      if (
        (!margin && typeof margin !== 'number') ||
        (!price && typeof price !== 'number') ||
        !compareTo(margin.margin, incoming.Margin, 0.0001) ||
        !compareTo(price, incoming.Price, 0.0001) ||
        mismatchInSpecs(incoming.Specs, product.propertyBag.properties) ||
        productLookupDescription?.toLowerCase() !== incomingDescription?.toLowerCase()
      ) {
        return true;
      }
    }
    return false;
  });

  // Filter all unaffected products
  return incomingProducts.filter((incoming: IncomingProduct) =>
    modifications.some((modification: IncomingProduct) => modification.ManufacturerSKU === incoming.ManufacturerSKU)
  );
};

export const UpdateStep = () => {
  const result = useResult<Result>();
  if (!result) throw Error('No result');

  const { skipToStep } = useControls();

  const [manufacturers] = useManufacturers();
  const [categories] = useCategories();
  const [regions] = useRegions();
  const [catalog] = useProducts();

  const addPriceAndMargin = useAddPriceAndMargin();

  const manufacturerMap = useMap(Maps.Manufacturer);
  const categoryMap = useMap(Maps.Category);
  const regionMap = useMap(Maps.Regions);

  const fullManufacturersMap: Record<string, string> = {
    ...manufacturerMap,
    ...manufacturers.reduce((acc, m) => ({ ...acc, [m.name]: m.id }), {})
  };

  const fullCategoriesMap: Record<string, string> = {
    ...categoryMap,
    ...categories.reduce((acc, m) => ({ ...acc, [m.name]: m.id }), {})
  };

  const fullRegionMap: Record<string, string> = {
    ...regionMap,
    ...regions.reduce((acc, m) => ({ ...acc, [m.code]: m.id }), {})
  };

  const IncomingProductToProduct = IncomingProduct.transform(
    toProduct(categories, fullCategoriesMap, manufacturers, fullManufacturersMap, regions, fullRegionMap)
  ).pipe(ExtendedBulkProduct);

  const [products, setProducts] = useState<ExtendedBulkProduct[]>(
    filterModifications(result.data, catalog, fullRegionMap).map(product => IncomingProductToProduct.parse(product))
  );

  const editProduct = useCallback(
    (recipe: (products: ExtendedBulkProduct[]) => void) => {
      return setProducts(existing => produce(existing, recipe));
    },
    [setProducts]
  );

  if (products.length === 0) {
    skipToStep('replace');
  }

  const bulkUpdateProducts = useBulkUpdateProducts();

  const finish = async () => {
    await bulkUpdateProducts.mutateAsync({
      products: products.map(p => ({
        ...p,
        manufacturerId: p.manufacturer.id,
        categoryId: p.category.id,
        regionId: p.region.id
      }))
    });

    toast.success('Product changes applied');
    skipToStep('replace');
  };

  return (
    <div className="flex flex-col gap-2">
      {result.data.length > 0 && <ProductReviewGrid products={products} editProduct={editProduct} />}
      <Button loading={addPriceAndMargin.isLoading} onClick={finish}>
        {result.data.length == 0 ? 'No updates, click to continue' : 'Apply'}
      </Button>
    </div>
  );
};
